import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronDown, X } from 'lucide-react';

const DocumentTree = ({ content, isOpen, onClose, editorRef }) => {
  const [headings, setHeadings] = useState([]);
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [activeHeadingId, setActiveHeadingId] = useState(null);
  const treeRef = useRef(null);

  // Parse headings from HTML content
  useEffect(() => {
    if (!content) {
      setHeadings([]);
      return;
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    const headingElements = doc.querySelectorAll('h1, h2, h3');
    
    const headingList = [];
    let currentH1 = null;
    let currentH2 = null;

    headingElements.forEach((heading, index) => {
      const level = parseInt(heading.tagName.charAt(1));
      const text = heading.textContent.trim();
      const id = `heading-${index}`;
      
      if (!text) return;

      if (level === 1) {
        currentH1 = {
          id,
          text,
          level,
          children: [],
          index,
        };
        headingList.push(currentH1);
        currentH2 = null;
      } else if (level === 2) {
        const h2Item = {
          id,
          text,
          level,
          children: [],
          index,
          parent: currentH1,
        };
        
        if (currentH1) {
          currentH1.children.push(h2Item);
        } else {
          headingList.push(h2Item);
        }
        currentH2 = h2Item;
      } else if (level === 3 && currentH2) {
        currentH2.children.push({
          id,
          text,
          level,
          index,
          parent: currentH2,
        });
      }
    });

    setHeadings(headingList);
    
    // Expand all sections by default
    const allIds = new Set();
    headingList.forEach((h1) => {
      allIds.add(h1.id);
      h1.children.forEach((h2) => {
        allIds.add(h2.id);
      });
    });
    setExpandedSections(allIds);
  }, [content]);

  // Track active heading while scrolling
  useEffect(() => {
    if (!isOpen || !editorRef?.current || headings.length === 0) return;

    const getEditorElement = () => {
      if (editorRef.current?.getEditorElement) {
        return editorRef.current.getEditorElement();
      }
      return null;
    };

    const editorElement = getEditorElement();
    if (!editorElement) return;

    const handleScroll = () => {
      const headingElements = editorElement.querySelectorAll('h1, h2, h3');
      let activeId = null;
      let closestHeading = null;
      let closestDistance = Infinity;

      headingElements.forEach((heading, index) => {
        const rect = heading.getBoundingClientRect();
        const editorRect = editorElement.getBoundingClientRect();
        
        // Check if heading is visible and near the top of viewport
        if (rect.top <= editorRect.top + 150 && rect.bottom >= editorRect.top - 50) {
          const distance = Math.abs(rect.top - editorRect.top);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestHeading = index;
          }
        }
      });

      if (closestHeading !== null && headings[closestHeading]) {
        // Match by index in flat structure
        const flatHeadings = [];
        const flatten = (items) => {
          items.forEach((item) => {
            flatHeadings.push(item);
            if (item.children) {
              flatten(item.children);
            }
          });
        };
        flatten(headings);
        
        if (flatHeadings[closestHeading]) {
          activeId = flatHeadings[closestHeading].id;
        }
      }

      setActiveHeadingId(activeId);
    };

    editorElement.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial check

    // Also check on content changes
    const observer = new MutationObserver(handleScroll);
    observer.observe(editorElement, { childList: true, subtree: true });

    return () => {
      editorElement.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, [isOpen, editorRef, headings]);

  const toggleSection = (id) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleHeadingClick = (heading) => {
    if (editorRef?.current?.scrollToHeading) {
      editorRef.current.scrollToHeading(heading.index);
    }
  };

  const renderHeading = (heading) => {
    const isExpanded = expandedSections.has(heading.id);
    const isActive = activeHeadingId === heading.id;
    const hasChildren = heading.children && heading.children.length > 0;

    return (
      <div key={heading.id} className="select-none">
        <div
          className={`flex items-center gap-1 px-2 py-1.5 rounded-md cursor-pointer transition-colors group ${
            isActive
              ? 'bg-primary-accent/20 text-primary-accent dark:text-primary-accent'
              : 'hover:bg-gray-100 dark:hover:bg-dark-200 text-gray-700 dark:text-light-200'
          }`}
          style={{ paddingLeft: `${(heading.level - 1) * 16 + 8}px` }}
          onClick={() => handleHeadingClick(heading)}
        >
          {hasChildren && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleSection(heading.id);
              }}
              className="p-0.5 hover:bg-gray-200 dark:hover:bg-dark-300 rounded transition-colors"
            >
              {isExpanded ? (
                <ChevronDown className="w-3 h-3" />
              ) : (
                <ChevronRight className="w-3 h-3" />
              )}
            </button>
          )}
          {!hasChildren && <div className="w-4" />}
          <span className={`text-sm flex-1 truncate ${heading.level === 1 ? 'font-semibold' : ''}`}>
            {heading.text || 'Untitled'}
          </span>
        </div>
        {hasChildren && isExpanded && (
          <div className="ml-2">
            {heading.children.map((child) => renderHeading(child))}
          </div>
        )}
      </div>
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={treeRef}
          initial={{ width: 0, opacity: 0 }}
          animate={{ width: 240, opacity: 1 }}
          exit={{ width: 0, opacity: 0 }}
          transition={{ 
            duration: 0.25, 
            ease: [0.4, 0, 0.2, 1],
            opacity: { duration: 0.2 }
          }}
          className="border-l border-gray-200 dark:border-dark-100 bg-white dark:bg-dark-200 flex flex-col overflow-hidden"
        >
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-dark-100 shrink-0">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-light-100">Document Outline</h3>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-100 dark:hover:bg-dark-300 rounded transition-colors text-gray-500 dark:text-light-500"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Tree Content */}
      <div className="flex-1 overflow-y-auto scrollbar-thin p-2">
        {headings.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-light-500 text-sm">
            <p>No headings found</p>
            <p className="text-xs mt-1">Add headings to see structure</p>
          </div>
        ) : (
          <div className="space-y-1">
            {headings.map((heading) => renderHeading(heading))}
          </div>
        )}
      </div>
    </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DocumentTree;
